import { useState } from "react";
import { Link } from "react-router-dom";

import { shop } from "@/api/shop";
import { ApiError } from "@/api/http";
import { useCart } from "@/cart/CartContext";
import { Price } from "@/components/Price";

export function Checkout() {
  const { lines } = useCart();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currency = lines[0]?.currency ?? "USD";
  const subtotal = lines.reduce((n, l) => n + l.price_cents * l.quantity, 0);

  async function pay() {
    setBusy(true);
    setError(null);
    try {
      const res = await shop.checkout(
        lines.map((l) => ({ sku_code: l.sku_code, quantity: l.quantity })),
      );
      window.location.href = res.checkout_url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Checkout failed. Please try again.");
      setBusy(false);
    }
  }

  if (lines.length === 0) {
    return (
      <div className="mx-auto max-w-lg">
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">Checkout</h1>
        <p className="mt-2 text-ink-soft">Your cart is empty.</p>
        <Link to="/" className="mt-4 inline-block font-medium text-brand-strong">
          Browse the catalog →
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg">
      <h1 className="text-2xl font-extrabold tracking-tight text-ink">Checkout</h1>
      <div className="mt-4 space-y-2 rounded-2xl border border-line bg-surface p-6 shadow-card">
        {lines.map((l) => (
          <div key={l.sku_code} className="flex justify-between text-sm">
            <span className="text-ink-soft">
              {l.name} <span className="text-ink-mute">× {l.quantity}</span>
            </span>
            <span className="font-medium text-ink">
              <Price cents={l.price_cents * l.quantity} currency={l.currency} />
            </span>
          </div>
        ))}
        <div className="mt-3 flex justify-between border-t border-line pt-3 text-base font-bold text-ink">
          <span>Total</span>
          <Price cents={subtotal} currency={currency} />
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-down">{error}</p>}

      <button
        onClick={pay}
        disabled={busy}
        className="mt-5 w-full rounded-lg bg-brand px-4 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-brand-strong disabled:opacity-60"
      >
        {busy ? "Redirecting to Stripe…" : "Pay securely"}
      </button>
      <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-ink-mute">
        🔒 You'll be redirected to Stripe's secure checkout. Final price is computed server-side.
      </p>
    </div>
  );
}
